import React, {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useContext,
  useMemo
} from 'react';
import { toInteger } from 'lodash';
import { StringParam, useQueryParams } from 'use-query-params';
import { authorisableUserWithSiteAdminRemoved } from 'policies/authorisable-user.helpers';
import useCurrentAreaAccess from 'services/providers/hooks/use-current-area-access';
import useCurrentOrganisationAccess from 'services/providers/hooks/use-current-organisation-access';
import useMarkingAccess from 'services/providers/hooks/use-marking-access';
import { accessContext } from './access.helpers';
import type { AccessContext } from './access.types';
import { useAuth } from './auth.provider';

const PageAccessContext = createContext(accessContext);

/**
 * A provider to that loads the neccessary data required to authorise the current page. If an
 * areaId is specified in the URL params, data to authorise the current area will also be loaded.
 */
export default function PageAccessProvider({ children }: PropsWithChildren): ReactElement {
  const { me, isLoading: isAuthLoading } = useAuth();

  const { belongsToMarkingOrganisation, isMarkingAccessLoading } = useMarkingAccess();
  const { currentOrganisationRoles } = useCurrentOrganisationAccess();

  // If an area ID is specified in the URL, also fetch the user's roles within that area:
  const [queryParams] = useQueryParams({
    areaId: StringParam
  });
  const areaId = toInteger(queryParams.areaId);
  const { currentArea, currentAreaRoles, belongsToCurrentArea, isCurrentAreaAccessLoading } =
    useCurrentAreaAccess(areaId);

  const isLoading = isAuthLoading || isMarkingAccessLoading || isCurrentAreaAccessLoading;

  // Authorisation focused user that can be passed to authorisation policies constructed in the
  // context of the marking and manage area sections, but could be expanded to authorise other
  // pages in more specific ways:
  const authorisableUser = useMemo(
    () => ({
      id: me?.id, // Can be undefined while loading, but should be set after.
      customerUser: me?.customerUser, // Acceptable to be undefined when not in organisation.
      roles: currentOrganisationRoles, // Should always be set, otherwise authorisation may fail.
      areaLevelRoles: currentAreaRoles,
      belongsToMarkingOrganisation, // Used for accessing Marking pages.
      belongsToCurrentArea
    }),
    [
      me?.id,
      me?.customerUser,
      currentOrganisationRoles,
      currentAreaRoles,
      belongsToMarkingOrganisation,
      belongsToCurrentArea
    ]
  );

  // A copy of the authorisableUser, but with the site admin role removed from the user's roles.
  // This is useful for comparative purposes (e.g. when showing the blue site admin only buttons):
  const userMinusSiteAdmin = useMemo(
    () => authorisableUserWithSiteAdminRemoved(authorisableUser),
    [authorisableUser]
  );

  // Authorisation focused area that can be passed to authorisation policies. Although there is not
  // always a current area to be authorised, this object is required to not be undefined so that
  // that relevant policies enforce an AuthorisableArea being passed to them:
  const authorisableArea = useMemo(
    () => ({
      id: currentArea?.id,
      identifier: currentArea?.identifier,
      private: currentArea?.private
    }),
    [currentArea]
  );

  const value = useMemo(
    () => ({ authorisableUser, userMinusSiteAdmin, authorisableArea, isLoading }),
    [authorisableUser, userMinusSiteAdmin, authorisableArea, isLoading]
  );

  return <PageAccessContext.Provider value={value}>{children}</PageAccessContext.Provider>;
}

export const usePageAccess = (): AccessContext => useContext(PageAccessContext);
